<script setup lang="ts">
/**
 * Openings (the grid family's `perOpening` parameter): clear height, listed top to bottom, for
 * the selected bay, with Even, the rebalance modes and per-row errors. With several bays a
 * segmented control picks the bay.
 */
import { computed } from 'vue';
import { bayCount, errorFor, fmtMm, layoutOf, mm, parseMm } from '@/domain';
import Icon from './Icon.vue';
import ProvChip from './ProvChip.vue';
import { snap, ui, viewerRef } from '@/app/state';
import { announce } from '@/app/announce';
import { bayField, commitOpening, setEvenTo } from '@/app/edits';
import { focusSoon } from '@/app/navigation';

const b = computed(() => snap.value.build);
const d = computed(() => snap.value.design);
const bays = computed(() => bayCount(d.value));
const bay = computed(() => Math.min(ui.bay, bays.value - 1));
const even = computed(() => layoutOf(d.value, bay.value).even);
const ofield = computed(() => snap.value.fields.openings ?? snap.value.fields.openingRatios ?? null);
const mine = computed(() => b.value.openings.filter((o) => (o.bay ?? 0) === bay.value));
/** Index of an opening of this bay in build.openings (the viewer's numbering). */
const globalIndex = (i: number) => b.value.openings.findIndex((o) => (o.bay ?? 0) === bay.value && o.index === i);
const rows = computed(() => mine.value.slice().reverse().map((o) => {
  const i = o.index;
  const key = bayField(bay.value, `opening-${i}`);
  const own = ui.fieldErrors[key] ?? null;
  return {
    i, o, key, own, err: own ?? errorFor(b.value, key),
    pos: i === mine.value.length - 1 ? 'top' : i === 0 ? 'bottom' : '',
    value: `op-${i}` in ui.drafts ? ui.drafts[`op-${i}`]! : fmtMm(o.clear),
    range: `${fmtMm(mm(o.bottomDatum))}–${fmtMm(mm(o.topDatum))}`,
  };
}));

function pickBay(i: number) {
  ui.bay = i;
  ui.openingSel = null;
  viewerRef.value?.highlightOpening(null);
  for (const k of Object.keys(ui.drafts)) if (k.startsWith('op-')) delete ui.drafts[k];
  focusSoon(`[data-bay-pick="${i}"]`);
}
function onInput(e: Event) { const t = e.target as HTMLInputElement; ui.drafts[t.id] = t.value; }
function commit(i: number, raw: string) { commitOpening(i, raw, `op-${i}`, bay.value); }
function onChange(e: Event, i: number) { const el = e.target as HTMLInputElement; if (el.isConnected) commit(i, el.value); }
function onKey(e: KeyboardEvent, i: number) {
  const el = e.target as HTMLInputElement;
  if (e.key === 'Enter') { e.preventDefault(); commit(i, el.value); return; }
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  const r = parseMm(el.value);
  if (!r.ok) return;
  e.preventDefault();
  const next = fmtMm(r.ticks + mm((e.shiftKey ? 10 : 1) * (e.key === 'ArrowUp' ? 1 : -1)));
  ui.drafts[el.id] = next;
  el.value = next;
  commit(i, next);
}
function onFocus(i: number) { ui.openingSel = i; viewerRef.value?.highlightOpening(globalIndex(i)); }
function onBlur() {
  setTimeout(() => {
    if (!(document.activeElement as HTMLElement | null)?.closest?.('.op')) { ui.openingSel = null; viewerRef.value?.highlightOpening(null); }
  }, 0);
}
function onEven(e: Event) { focusSoon('#evenSw'); setEvenTo((e.target as HTMLInputElement).checked, bay.value); }
function setRebalance(m: 'neighbour' | 'none') {
  ui.rebalance = m;
  announce(m === 'none' ? 'Only the opening you change will move. You place the rest.' : 'The opening above adjusts when you change one.');
}
</script>

<template>
  <section class="sec" aria-labelledby="opsH">
    <div class="sec-head">
      <h2 id="opsH" class="h-sec">Openings <span class="muted">clear height, top to bottom{{ bays > 1 ? `, bay ${bay + 1}` : '' }}</span></h2>
      <ProvChip v-if="ofield" :status="ofield.status" compact />
    </div>
    <div v-if="bays > 1" class="seg seg--sm seg--bays" role="radiogroup" aria-label="Bay to edit">
      <button v-for="i in bays" :key="i" type="button" role="radio" :aria-checked="bay === i - 1" :data-bay-pick="i - 1" @click="pickBay(i - 1)">Bay {{ i }}</button>
    </div>
    <div class="op-tools">
      <label class="switch"><input id="evenSw" type="checkbox" role="switch" :checked="even" @change="onEven"><span class="sw" aria-hidden="true" /><span>Even</span></label>
      <div class="seg seg--sm" role="radiogroup" aria-label="When you change an opening">
        <button type="button" role="radio" :aria-checked="ui.rebalance === 'neighbour'" data-rebalance="neighbour" title="Like moving a shelf: the opening above adjusts" @click="setRebalance('neighbour')">Neighbour adjusts</button>
        <button type="button" role="radio" :aria-checked="ui.rebalance === 'none'" data-rebalance="none" title="Only this opening changes; you place the rest" @click="setRebalance('none')">Only this one</button>
      </div>
    </div>
    <ol class="ops">
      <li v-for="r in rows" :key="r.i" class="op" :class="{ 'is-sel': ui.openingSel === r.i, 'has-err': !!r.err }" :data-op="r.i">
        <label class="op-l" :for="`op-${r.i}`"><span class="op-n" aria-hidden="true">{{ r.o.number }}</span><span>Opening {{ r.o.number }}<span v-if="r.pos" class="muted"> ({{ r.pos }})</span></span></label>
        <div class="input-unit input-unit--sm">
          <input
            :id="`op-${r.i}`"
            class="input mono"
            inputmode="decimal"
            autocomplete="off"
            :value="r.value"
            :aria-describedby="`opr-${r.i}${r.err ? ` err-${r.key}` : ''}`"
            :aria-invalid="r.err ? 'true' : undefined"
            @input="onInput"
            @change="onChange($event, r.i)"
            @keydown="onKey($event, r.i)"
            @focus="onFocus(r.i)"
            @blur="onBlur"
          ><span>mm</span>
        </div>
        <span :id="`opr-${r.i}`" class="op-range mono" title="Shelf positions from the floor">{{ r.range }}</span>
        <p v-if="r.err" :id="`err-${r.key}`" class="field-err" :role="r.own ? 'alert' : undefined"><Icon name="alert" /><span>{{ r.err }}</span></p>
      </li>
    </ol>
  </section>
</template>
